
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import LinkTokenCreate from '../../../components/docs/LinkTokenCreate';
export const metadata = {
  toc: true,
  layout: 'reference',
  source: 'endpoint',
  secondLevelToc: true,
  alwaysExpand: true,
  parentTocLevel: 3,
  childTocLevel: 4,
  description: 'API reference for Link tokens and Link webhooks'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const Schema = makeShortcode("Schema");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Link endpoints' subtitle='API reference for Link tokens and webhooks' mdxType="Header" />
    <Callout mdxType="Callout">
      <p>{`This page covers the backend flow for creating and managing Link tokens. For the Link frontend, see `}<a parentName="p" {...{
          "href": "/docs/link"
        }}>{`Link docs`}</a>{`.`}</p>
    </Callout>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Endpoints`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/link/token/create`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Create a token for initializing a Link session`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/link/token/get`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Get the public token and other details about a completed Link session`}</td>
        </tr>
      </tbody>
    </table>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`See also`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/item/public_token/exchange`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Exchange a public token for an access token`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/sandbox/public_token/create`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Create a public token without the Link flow (Sandbox only)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/sandbox/item/reset_login`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Force an Item into an error state (Sandbox only)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/session/token/create`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Create a session (Layer only)`}</td>
        </tr>
      </tbody>
    </table>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Webhooks`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "#item_add_result"
            }}><inlineCode parentName="a">{`ITEM_ADD_RESULT`}</inlineCode></a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An Item was added during a Link session`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "#events"
            }}><inlineCode parentName="a">{`EVENTS`}</inlineCode></a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Events were received from a Hosted Link session`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "#session_finished"
            }}><inlineCode parentName="a">{`SESSION_FINISHED`}</inlineCode></a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The user has completed a Link session`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "endpoints"
    }}>{`Endpoints`}</h3>
    <h4 {...{
      "id": "linktokencreate"
    }}>{`/link/token/create`}</h4>
    <Schema method='POST' route='/link/token/create' type='request' mdxType="Schema">
      <LinkTokenCreate useCases={[{
        useCase: 'Personal Finance or Budgeting (Transactions)',
        comment: "Account filtering isn't required here, but sometimes it's helpful to see an example.",
        config: {
          user: {
            client_user_id: 'user-id',
            phone_number: '+14155550123'
          },
          client_name: 'Personal Finance App',
          products: ['transactions'],
          transactions: {
            days_requested: 730
          },
          country_codes: ['US'],
          language: 'en',
          webhook: 'https://sample-web-hook.com',
          redirect_uri: 'https://domainname.com/oauth-page.html',
          account_filters: {
            depository: {
              account_subtypes: ['checking', 'savings']
            },
            credit: {
              account_subtypes: ['credit card']
            }
          }
        }
      }, {
        useCase: 'Personal Finance or Budgeting (Transactions) with Multi-Item Link',
        comment: "Generate the user_id by calling /user/create first. Since onSuccess won't contain public tokens in a Multi-Item Link session, you'll also need to handle the SESSION_FINISHED webhook (or call /link/token/get) and exchange each token in its public_tokens array.",
        config: {
          user_id: 'usr_9nSp2KuZ2x4JDw',
          enable_multi_item_link: true,
          client_name: 'Personal Finance App',
          products: ['transactions'],
          transactions: {
            days_requested: 730
          },
          country_codes: ['US'],
          language: 'en',
          webhook: 'https://sample-web-hook.com',
          redirect_uri: 'https://domainname.com/oauth-page.html',
          account_filters: {
            depository: {
              account_subtypes: ['checking', 'savings']
            },
            credit: {
              account_subtypes: ['credit card']
            }
          }
        }
      }, {
        useCase: 'ACH risk management (Auth and/or Signal Transaction Scores, Identity)',
        config: {
          user: {
            client_user_id: 'user-abc',
            phone_number: '+14155550123',
            email_address: 'user@example.com'
          },
          products: ['auth', 'signal'],
          required_if_supported_products: ['identity'],
          client_name: 'Money Transfer App',
          language: 'en',
          country_codes: ['US'],
          webhook: 'https://sample-web-hook.com',
          redirect_uri: 'https://domainname.com/oauth-page.html',
          auth: {
            automated_microdeposits_enabled: true
          }
        }
      }, {
        useCase: 'Money Movement (Transfer)',
        config: {
          user: {
            client_user_id: 'user-abc',
            email_address: 'user@example.com'
          },
          products: ['transfer'],
          client_name: 'Money Transfer App',
          language: 'en',
          country_codes: ['US'],
          webhook: 'https://sample-web-hook.com',
          redirect_uri: 'https://domainname.com/oauth-page.html',
          auth: {
            automated_microdeposits_enabled: true
          }
        }
      }, {
        useCase: 'Brokerage-to-brokerage transfers (Investments Move)',
        config: {
          user: {
            client_user_id: 'user-abc',
            email_address: 'user@example.com'
          },
          products: ['investments_auth'],
          client_name: 'Gopher Brokerage',
          language: 'en',
          country_codes: ['US']
        }
      }, {
        useCase: 'Instant onboarding (Layer)',
        config: {
          user: {
            client_user_id: 'user-abc'
          },
          products: ['layer'],
          client_name: 'My Money Transfer App',
          language: 'en',
          country_codes: ['US']
        }
      }, {
        useCase: 'Identity Verification and AML (IDV, Monitor)',
        comment: "Generate the template_id from within the IDV Dashboard",
        config: {
          user: {
            client_user_id: 'user-abc',
            email_address: 'user@example.com'
          },
          products: ['identity_verification'],
          identity_verification: {
            template_id: 'idvtmp_123456789abcdef'
          },
          client_name: 'My Money Transfer App',
          language: 'en',
          country_codes: ['US']
        }
      }, {
        useCase: 'Consumer Report for credit and lending (Consumer Report by Plaid Check)',
        comment: "Generate the user_id by calling /user/create",
        config: {
          user: {
            client_user_id: 'user-abc',
            email_address: 'user@example.com'
          },
          user_id: 'usr_9nSp2KuZ2x4JDw',
          products: ['cra_income_insights', 'cra_base_report'],
          consumer_report_permissible_purpose: 'EXTENSION_OF_CREDIT',
          cra_options: {
            days_requested: 365
          },
          client_name: 'The Loan Arranger',
          language: 'en',
          country_codes: ['US'],
          webhook: 'https://sample-web-hook.com'
        }
      }, {
        useCase: '(Legacy link token interface) Consumer Report for credit and lending (Consumer Report by Plaid Check)',
        comment: "Generate the user_token by calling /user/create",
        config: {
          user: {
            client_user_id: 'user-abc',
            email_address: 'user@example.com'
          },
          user_token: 'user-environment-12345678-abcd-4bcd-abcd-1234567890ab',
          products: ['cra_income_insights', 'cra_base_report'],
          consumer_report_permissible_purpose: 'EXTENSION_OF_CREDIT',
          cra_options: {
            days_requested: 365
          },
          client_name: 'The Loan Arranger',
          language: 'en',
          country_codes: ['US'],
          webhook: 'https://sample-web-hook.com'
        }
      }, {
        useCase: 'Income Verification - Bank Income (Income)',
        comment: "Generate the user_id by calling /user/create",
        config: {
          user: {
            client_user_id: 'user-abc',
            email_address: 'user@example.com'
          },
          user_id: 'usr_9nSp2KuZ2x4JDw',
          products: ['income_verification'],
          income_verification: {
            income_source_types: ['bank'],
            bank_income: {
              days_requested: 120
            }
          },
          enable_multi_item_link: true,
          client_name: 'Loan Approval App',
          language: 'en',
          country_codes: ['US'],
          webhook: 'https://sample-web-hook.com',
          redirect_uri: 'https://domainname.com/oauth-page.html'
        }
      }, {
        useCase: '(Legacy link token interface) Income Verification - Bank Income (Income)',
        comment: "Generate the user_token by calling /user/create",
        config: {
          user: {
            client_user_id: 'user-abc',
            email_address: 'user@example.com'
          },
          user_token: 'user-environment-12345678-abcd-4bcd-abcd-1234567890ab',
          products: ['income_verification'],
          income_verification: {
            income_source_types: ['bank'],
            bank_income: {
              days_requested: 120
            }
          },
          enable_multi_item_link: true,
          client_name: 'Loan Approval App',
          language: 'en',
          country_codes: ['US'],
          webhook: 'https://sample-web-hook.com',
          redirect_uri: 'https://domainname.com/oauth-page.html'
        }
      }, {
        useCase: 'Income Verification - Document only (Income)',
        comment: "Generate the user_id by calling /user/create",
        config: {
          user: {
            client_user_id: 'user-abc',
            email_address: 'user@example.com'
          },
          user_id: 'usr_9nSp2KuZ2x4JDw',
          products: ['income_verification'],
          income_verification: {
            income_source_types: ['payroll'],
            payroll_income: {
              flow_types: ['payroll_document_income']
            }
          },
          client_name: 'Loan Approval App',
          language: 'en',
          country_codes: ['US'],
          webhook: 'https://sample-web-hook.com'
        }
      }, {
        useCase: '(Legacy link token interface) Income Verification - Document only (Income)',
        comment: "Generate the user_token by calling /user/create",
        config: {
          user: {
            client_user_id: 'user-abc',
            email_address: 'user@example.com'
          },
          user_token: 'user-environment-12345678-abcd-4bcd-abcd-1234567890ab',
          products: ['income_verification'],
          income_verification: {
            income_source_types: ['payroll'],
            payroll_income: {
              flow_types: ['payroll_document_income']
            }
          },
          client_name: 'Loan Approval App',
          language: 'en',
          country_codes: ['US'],
          webhook: 'https://sample-web-hook.com'
        }
      }, {
        useCase: 'Income Verification - Payroll only (Income)',
        comment: "Generate the user_id by calling /user/create",
        config: {
          user: {
            client_user_id: 'user-abc',
            email_address: 'user@example.com'
          },
          user_id: 'usr_9nSp2KuZ2x4JDw',
          products: ['income_verification'],
          income_verification: {
            income_source_types: ['payroll'],
            payroll_income: {
              flow_types: ['payroll_digital_income']
            }
          },
          client_name: 'Loan Approval App',
          language: 'en',
          country_codes: ['US'],
          webhook: 'https://sample-web-hook.com'
        }
      }, {
        useCase: '(Legacy link token interface) Income Verification - Payroll only (Income)',
        comment: "Generate the user_token by calling /user/create",
        config: {
          user: {
            client_user_id: 'user-abc',
            email_address: 'user@example.com'
          },
          user_token: 'user-environment-12345678-abcd-4bcd-abcd-1234567890ab',
          products: ['income_verification'],
          income_verification: {
            income_source_types: ['payroll'],
            payroll_income: {
              flow_types: ['payroll_digital_income']
            }
          },
          client_name: 'Loan Approval App',
          language: 'en',
          country_codes: ['US'],
          webhook: 'https://sample-web-hook.com'
        }
      }, {
        useCase: 'Loan Underwriting (Assets)',
        config: {
          user: {
            client_user_id: 'user-abc',
            email_address: 'user@example.com'
          },
          products: ['assets'],
          client_name: 'Loan Approval App',
          language: 'en',
          country_codes: ['US'],
          webhook: 'https://sample-web-hook.com',
          redirect_uri: 'https://domainname.com/oauth-page.html'
        }
      }, {
        useCase: 'Loan Underwriting (Statements)',
        config: {
          user: {
            client_user_id: 'user-abc',
            email_address: 'user@example.com'
          },
          products: ['statements'],
          statements: {
            start_date: '2024-01-01',
            end_date: '2024-04-01'
          },
          client_name: 'Loan Approval App',
          language: 'en',
          country_codes: ['US'],
          webhook: 'https://sample-web-hook.com',
          redirect_uri: 'https://domainname.com/oauth-page.html'
        }
      }, {
        useCase: 'Loan Underwriting (Assets and Statements)',
        config: {
          user: {
            client_user_id: 'user-abc',
            email_address: 'user@example.com'
          },
          products: ['assets'],
          required_if_supported_products: ['statements'],
          statements: {
            start_date: '2024-01-01',
            end_date: '2024-04-01'
          },
          client_name: 'Loan Approval App',
          language: 'en',
          country_codes: ['US'],
          webhook: 'https://sample-web-hook.com',
          redirect_uri: 'https://domainname.com/oauth-page.html'
        }
      }, {
        useCase: "Managing a user's investments (Investments)",
        config: {
          user: {
            client_user_id: 'user-abc',
            email_address: 'user@example.com'
          },
          products: ['investments'],
          client_name: 'Investment Tracker',
          language: 'en',
          country_codes: ['US'],
          webhook: 'https://sample-web-hook.com',
          redirect_uri: 'https://domainname.com/oauth-page.html'
        }
      }, {
        useCase: 'Debt management (Liabilities)',
        config: {
          user: {
            client_user_id: 'user-abc',
            email_address: 'user@example.com'
          },
          products: ['liabilities'],
          client_name: 'Loan Payment Helper',
          language: 'en',
          country_codes: ['US'],
          webhook: 'https://sample-web-hook.com',
          redirect_uri: 'https://domainname.com/oauth-page.html'
        }
      }, {
        useCase: 'Money Movement in the EU or UK (Payment Initiation)',
        comment: "Call /payment_initiation/recipient/create and /user/create to get the recipient_id and user_id, then generate the payment id by calling /payment_initiation/payment/create",
        config: {
          payment_initiation: {
            "payment_id": "${PAYMENT_ID}"
          },
          user_id: '${USER_ID}',
          user: {
            client_user_id: 'user-abc',
            email_address: 'user@example.com'
          },
          products: ['payment_initiation'],
          client_name: 'My Money Transfer App',
          language: 'en',
          country_codes: ['GB'],
          webhook: 'https://sample-web-hook.com',
          redirect_uri: 'https://domainname.com/oauth-page.html'
        }
      }, {
        useCase: 'Update mode: Repair Item',
        config: {
          access_token: "access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175",
          user: {
            client_user_id: 'user-id',
            phone_number: '+14155550123'
          },
          client_name: 'Personal Finance App',
          country_codes: ['US'],
          language: 'en',
          webhook: 'https://sample-web-hook.com',
          redirect_uri: 'https://domainname.com/oauth-page.html'
        }
      }, {
        useCase: 'Update mode: Add accounts to existing Item',
        comment: "Account filtering isn't required here, but sometimes it's helpful to see an example.",
        config: {
          access_token: "access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175",
          update: {
            "account_selection_enabled": true
          },
          account_filters: {
            depository: {
              account_subtypes: ['checking', 'savings']
            },
            credit: {
              account_subtypes: ['credit card']
            }
          },
          user: {
            client_user_id: 'user-id',
            phone_number: '+14155550123'
          },
          client_name: 'Personal Finance App',
          country_codes: ['US'],
          language: 'en',
          webhook: 'https://sample-web-hook.com',
          redirect_uri: 'https://domainname.com/oauth-page.html'
        }
      }]} mdxType="LinkTokenCreate" />
    </Schema>
    <Schema method='POST' route='/link/token/create' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "linktokenget"
    }}>{`/link/token/get`}</h4>
    <Schema method='POST' route='/link/token/get' type='request' mdxType="Schema">
      <MDXCodeBlock title="/link/token/get" type='server' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::LinkTokenGetRequest.new({ link_token: link_token })
response = client.link_token_get(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`linkTokenGetRequest := plaid.NewLinkTokenGetRequest(linkToken)
linkTokenGetResp, _, err := client.PlaidApi.LinkTokenGet(ctx).LinkTokenGetRequest(*linkTokenGetRequest).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`linkTokenGetRequest := &plaid.LinkTokenGetRequest{LinkToken: linkToken}
linkTokenGetResp, err := client.Link.GetToken(ctx, linkTokenGetRequest)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`LinkTokenGetRequest request = new LinkTokenGetRequest()
  .linkToken(linkToken);
Response<LinkTokenGetResponse> getResponse = client()
  .linkTokenGet(request)
  .execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = LinkTokenGetRequest(link_token=link_token)
response = client.link_token_get(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: LinkTokenGetRequest = {
  link_token: linkToken,
};
try {
  const response = await plaidClient.linkTokenGet(request);
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/link/token/get -H 'Content-Type: application/json' -d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "link_token": "\${LINK_TOKEN}"
}'
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/link/token/get' type='response' mdxType="Schema" />
    <h3 {...{
      "id": "webhooks"
    }}>{`Webhooks`}</h3>
    <h4 {...{
      "id": "item_add_result"
    }}><inlineCode parentName="h4">{`ITEM_ADD_RESULT`}</inlineCode></h4>
    <Schema model='ItemAddResultWebhook' type='model' mdxType="Schema" />
    <h4 {...{
      "id": "events"
    }}><inlineCode parentName="h4">{`EVENTS`}</inlineCode></h4>
    <Schema model='LinkEventsWebhook' type='model' mdxType="Schema" />
    <h4 {...{
      "id": "session_finished"
    }}><inlineCode parentName="h4">{`SESSION_FINISHED`}</inlineCode></h4>
    <Schema model='LinkSessionFinishedWebhook' type='model' mdxType="Schema" />
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "endpoints",
        level: 3,
        title: "Endpoints",
      },
{
        id: "linktokencreate",
        level: 4,
        title: "/link/token/create",
      },
{
        id: "linktokenget",
        level: 4,
        title: "/link/token/get",
      },
{
        id: "webhooks",
        level: 3,
        title: "Webhooks",
      },
{
        id: "item_add_result",
        level: 4,
        title: "ITEM_ADD_RESULT",
      },
{
        id: "events",
        level: 4,
        title: "EVENTS",
      },
{
        id: "session_finished",
        level: 4,
        title: "SESSION_FINISHED",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
